import CID from 'cids'
import React, { useEffect, useState } from 'react'
import Tilt from 'react-parallax-tilt'
import Sparkle from 'react-sparkle'
import styled from 'styled-components/macro'

import { USELESS_NFT_ADDRESSES } from '../../constants/addresses'
import { SupportedChainId } from '../../constants/chains'
import useActiveWeb3React from '../../hooks/useActiveWeb3React'
import { useUselessNft } from '../../hooks/useContract'
import useUselessToken from '../../hooks/useUselessToken'
import useBlockNumber from '../../lib/hooks/useBlockNumber'
import { useIsRandomNumberSet, useIsSaleOpen } from '../../state/mint/hooks'
import { useTokenBalance } from '../../state/wallet/hooks'

const PageWrapper = styled.div`
  width: 100%;
  max-width: 100vw;
  overflow: hidden;

  @media screen and (max-width: 400px) {
    margin-top: -5vh;
  }
`

const TopSection = styled.div`
  width: 100%;
  text-align: center;
  margin-bottom: 50px;
  margin-top: 2vh;
`

const TitleWrapper = styled.div`
  width: 100%;
  max-width: 800px;
  display: inline-block;
`

const SloganText = styled.div`
  font-weight: 400;
  font-size: 64px;
  margin-bottom: 20px;
`

const SubtitleText = styled.div`
  font-weight: 200;
  font-size: 17px;
  margin-bottom: 15px;
`

const NoNFTs = styled.div`
  width: fit-content;
  margin: 0 auto;
  font-size: 22px;
  line-height: 36px;
  font-weight: 200;
  margin-top: 10vh;
  text-align: center;
`

const NFTsSection = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
`

const NFTWrapper = styled.div`
  width: fit-content;
  display: inline-block;
  vertical-align: top;
  margin: 0 25px 50px 25px;
  position: relative;

  > span {
    z-index: 9999;
  }

  img {
    width: 100%;
    max-width: 800px;
    height: auto;
    filter: drop-shadow(2px 3px 14px rgb(0 0 0 / 50%));
    margin-bottom: -4px;
    cursor: pointer;

    @media screen and (max-width: 880px) {
      max-width: calc(100vw - 150px);
    }

    @media screen and (max-width: 500px) {
      max-width: 260px;
    }
  }
`

const ImageWrapper = styled.div`
  width: fit-content;
  height: fit-content;
  cursor: pointer;
  border-radius: 10px;

  > * > div {
    border-radius: 7% !important;
  }
`

const NFTInfo = styled.div`
  width: 100%;
  font-size: 16px;
  text-align: left;
  display: flex;
  justify-content: space-between;
  margin-top: 4px;
`

enum Tier {
  DIAMOND = 'Diamond',
  GOLD = 'Gold',
  SILVER = 'Silver',
  BRONZE = 'Bronze',
  PLEB = 'Pleb',
  NOT_REVEALED = 'Not Revealed Yet',
}

const Rarity = styled.div<{ rarity: Tier }>`
  color: ${({ rarity }) =>
    rarity === Tier.DIAMOND
      ? '#E5E4E2'
      : rarity === Tier.GOLD
      ? '#FFD700'
      : rarity === Tier.SILVER
      ? '#C0C0C0'
      : rarity === Tier.BRONZE
      ? '#b8860b'
      : 'white'};
  font-weight: 500;
  font-size: 20px;
  width: fit-content;
  display: inline-block;
  vertical-align: top;
`

const Number = styled.div`
  font-weight: 200;
  color: ${({ theme }) => theme.text1};
  width: fit-content;
  display: inline-block;
  vertical-align: top;
`

interface NFT {
  number: number
  name: string
  description: string
  image: string | undefined
  tier: Tier
  attributes: Record<string, string>[]
}

const default_image = 'http://placekitten.com/50/50' //TODO - replace with local file that acts as a temp image
// TODO - add image for when NFT tiers not revealed
// TODO - add no NFTs state
// TODO - wallet not connected state

export default function MyNFTs() {
  const openInNewTab = (url: string) => {
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer')
    if (newWindow) {
      newWindow.opener = null
    }
  }

  const { account } = useActiveWeb3React()
  const blockNumber = useBlockNumber()

  const uselessNftContract = useUselessNft()
  const uselessToken = useUselessToken()
  const userBalance = useTokenBalance(account ?? undefined, uselessToken)
  const isSaleOpen = useIsSaleOpen(uselessNftContract)
  const isRandomNumberSet = useIsRandomNumberSet(uselessNftContract)

  const [nftList, setNftList] = useState<NFT[]>([])

  useEffect(() => {
    if (!userBalance || userBalance.equalTo('0') || !account || !uselessNftContract) {
      setNftList([])
      return
    }

    const userBalanceNumber = parseInt(userBalance.toFixed(0))
    const nftListPromises = Array(userBalanceNumber)
      .fill(0)
      .map((_, i) => {
        return uselessNftContract
          .tokenOfOwnerByIndex(account, i)
          .then((id) => {
            return uselessNftContract.tokenURI(id).then((uri) => ({ id, uri }))
          })
          .then(({ id, uri }) => {
            const baseCid0 = uri.match(/ipfs:\/\/(.+)\//)?.[1]
            const idWithExtension = uri.match(/ipfs:\/\/.+\/(.+)/)?.[1]
            if (!baseCid0 || !idWithExtension) {
              const error = `Invalid CID-0 for ID ${id.toString()}, found ${uri}`
              console.error(error)
              throw new Error(error)
            }

            const baseCid1 = new CID(baseCid0).toV1()
            return fetch(`https://${baseCid1.toString('base32')}.ipfs.infura-ipfs.io/${idWithExtension.toString()}`)
              .then((response) => {
                if (response.status === 200) {
                  return response.json()
                } else {
                  // some instances may not use the extension of "_{tierNumber}.json"
                  return Promise.reject()
                }
              })
              .catch(() =>
                fetch(`https://${baseCid1.toString('base32')}.ipfs.infura-ipfs.io/${id.toString()}`).then((response) =>
                  response.json()
                )
              )
              .then((json) => ({ id: id.toString(), ...json }))
          })
          .then<NFT>((json) => {
            const imageCid0 = json.image.match(/ipfs:\/\/(.+)\/.+/)?.[1]
            if (!imageCid0) {
              return Promise.reject(new Error(`Invalid CID-0 for image at ID ${json.id}, found ${json.image}`))
            }
            const imageCid1 = new CID(imageCid0).toV1()
            let extension = json.image.match(/ipfs:\/\/.+(\/.+)/)?.[1]
            if (!extension) {
              extension = ''
            }
            const tier = json.attributes.find((attr: any) => attr?.trait_type === 'Tier')?.value

            return {
              number: parseInt(json.id),
              name: json.name,
              description: json.description,
              image: `https://${imageCid1.toString('base32')}.ipfs.infura-ipfs.io${extension}`,
              attributes: json.attributes,
              tier: isSaleOpen || !isRandomNumberSet ? Tier.NOT_REVEALED : tier ?? Tier.PLEB,
            }
          })
      })

    Promise.all(nftListPromises)
      .then((nftList) => {
        setNftList(nftList)
      })
      .catch((error) => {
        console.error('Found error:', error)
      })
  }, [blockNumber, account, userBalance, uselessNftContract, isSaleOpen, isRandomNumberSet])

  return (
    <PageWrapper>
      <TopSection>
        <TitleWrapper>
          <SloganText>Your Useless NFTs</SloganText>
          <SubtitleText>{`A testament to wasted money.`}</SubtitleText>
        </TitleWrapper>
      </TopSection>
      {!account ? (
        <NoNFTs>Please connect your wallet to view your Useless NFTs</NoNFTs>
      ) : userBalance && userBalance.equalTo('0') ? (
        <NoNFTs>
          {`Doesn't look like you have any Useless NFTs.`}
          <br />
          {`Good for you. Smart move.`}
        </NoNFTs>
      ) : nftList.length === 0 ? (
        <NoNFTs>Your Useless NFTs are loading...</NoNFTs>
      ) : (
        <NFTsSection>
          {nftList.map((nft, key) => (
            <NFTWrapper key={key}>
              {nft.tier === Tier.DIAMOND && <Sparkle />}
              <ImageWrapper
                onClick={() =>
                  openInNewTab(
                    `https://opensea.io/assets/${USELESS_NFT_ADDRESSES[SupportedChainId.MAINNET]}/${nft.number}`
                  )
                }
              >
                <Tilt
                  tiltReverse={true}
                  tiltMaxAngleX={8}
                  tiltMaxAngleY={8}
                  glareEnable={true}
                  style={{ width: 'fit-content', height: 'fit-content', margin: '0 auto' }}
                >
                  <img src={nft.image || default_image} alt={''} />
                </Tilt>
              </ImageWrapper>
              <NFTInfo>
                <Rarity rarity={nft.tier}>{nft.tier}</Rarity>
                <Number>{nft.number}</Number>
              </NFTInfo>
            </NFTWrapper>
          ))}
        </NFTsSection>
      )}
    </PageWrapper>
  )
}
